import { SystemSettingsTab, SystemSettingsTemplateTab, systemSettingsPath } from "@/router/SystemSettingsRoutes";
import { LeafTranslationKeys, TranslationType } from "@/translations";

export enum SystemSettingsGroup {
  PersonsAndAccess = "persons-and-access",
  Communication = "communication",
  MasterData = "master-data",
  Configuration = "configuration",
}

type SettingsLabelKey = LeafTranslationKeys<Pick<TranslationType, "settings">>;

export interface SystemSettingsNavEntry {
  label: SettingsLabelKey;
  to: string;
}

export interface SystemSettingsNavGroup {
  group: SystemSettingsGroup;
  label: SettingsLabelKey;
  // Non-empty: every group has at least one entry, so `entries[0]` (the group's landing page) is safe.
  entries: [SystemSettingsNavEntry, ...SystemSettingsNavEntry[]];
}

// Single source of truth for the grouped settings navigation. Order of groups and of the entries
// within each group is fixed by the spec (WI 1518). Every entry links to an already-existing route,
// so grouping is purely presentational and leaves each setting page's functionality untouched.
export const systemSettingsNav: [SystemSettingsNavGroup, ...SystemSettingsNavGroup[]] = [
  {
    group: SystemSettingsGroup.PersonsAndAccess,
    label: "settings.groups.personsAndAccess",
    entries: [
      { label: "settings.tabs.counselors", to: systemSettingsPath.byTab(SystemSettingsTab.Counselors) },
      { label: "settings.tabs.volunteers", to: systemSettingsPath.byTab(SystemSettingsTab.Volunteers) },
    ],
  },
  {
    group: SystemSettingsGroup.Communication,
    label: "settings.groups.communication",
    entries: [
      { label: "settings.tabs.smsTemplates", to: systemSettingsPath.byTemplateTab(SystemSettingsTemplateTab.Sms) },
      { label: "settings.tabs.emailTemplates", to: systemSettingsPath.byTemplateTab(SystemSettingsTemplateTab.Email) },
      { label: "settings.tabs.banners", to: systemSettingsPath.byTab(SystemSettingsTab.Banners) },
      { label: "settings.tabs.translationTool", to: systemSettingsPath.byTab(SystemSettingsTab.TranslationTool) },
    ],
  },
  {
    group: SystemSettingsGroup.MasterData,
    label: "settings.groups.masterData",
    entries: [
      {
        label: "settings.tabs.caseNoteTemplates",
        to: systemSettingsPath.byTemplateTab(SystemSettingsTemplateTab.CaseNote),
      },
      { label: "settings.tabs.courts", to: systemSettingsPath.byTab(SystemSettingsTab.Courts) },
      {
        label: "settings.tabs.proceedingParticipants",
        to: systemSettingsPath.byTab(SystemSettingsTab.ProceedingParticipants),
      },
      { label: "settings.tabs.addresses", to: systemSettingsPath.byTab(SystemSettingsTab.Addresses) },
    ],
  },
  {
    group: SystemSettingsGroup.Configuration,
    label: "settings.groups.configuration",
    entries: [
      { label: "settings.tabs.dropdowns", to: systemSettingsPath.byTab(SystemSettingsTab.Dropdowns) },
      {
        label: "settings.tabs.systemRelevantDropdowns",
        to: systemSettingsPath.byTab(SystemSettingsTab.SystemRelevantDropdowns),
      },
      { label: "settings.tabs.parameters", to: systemSettingsPath.byTab(SystemSettingsTab.Parameters) },
      { label: "settings.tabs.amsParameters", to: systemSettingsPath.byTab(SystemSettingsTab.AmsParameters) },
      { label: "settings.tabs.holidays", to: systemSettingsPath.byTab(SystemSettingsTab.Holidays) },
      { label: "settings.tabs.mailMerge", to: systemSettingsPath.byTab(SystemSettingsTab.MailMerge) },
    ],
  },
];
